import React from 'react';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { observer } from 'mobx-react-lite';
import { ApplicationSdk } from '../../../core/sdk';
import { InputText } from 'primereact/inputtext';
import { PickList } from 'primereact/picklist';
import 'react-image-crop/dist/ReactCrop.css';
import { Calendar } from 'primereact/calendar';
import { Checkbox } from 'primereact/checkbox';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Tooltip } from 'primereact/tooltip';
import { TabView, TabPanel } from 'primereact/tabview';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Tag } from 'primereact/tag';
import { MultiSelect } from 'primereact/multiselect';

const EmployeeRights = observer(({ store }: { store: any }) => {
    return (
        <>
            <div style={{ marginBottom: '1rem', width: '100%' }}>
                <label
                    htmlFor="mail"
                    style={{ display: 'block', marginBottom: '0.5rem' }}
                >
                    Rechte
                </label>
                <MultiSelect
                    id="rights"
                    value={store.selectedRights}
                    onChange={(e: any) => store.onRightsChange(e.value)}
                    options={store.allAvailableRights}
                    optionLabel="label"
                    data-testid={'employee-input-permissions'}
                    placeholder="Rechte wählen"
                    style={{ width: '100%', maxWidth: '100%' }}
                    selectAllLabel="Alle Rechte zuweisen"
                />
            </div>
        </>
    );
});

const EmployeeObjectList = observer(({ store }: { store: any }) => {
    const itemTemplate = (item: any) => {
        return (
            <div className="flex flex-wrap p-2 align-items-center gap-3">
                <div className="flex-1 flex flex-column gap-2">
                    <span style={{ textAlign: 'left' }}>{item.name}</span>
                </div>
            </div>
        );
    };

    const onChange = (event: any) => {
        store.updateEmployeeAssignment(event.source, event.target);
    };

    return (
        <div style={{ marginTop: '2rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem' }}>
                {!store.isAvailableForAll ? (
                    <PickList
                        dataKey="id"
                        source={store.unassignedObjectsArray}
                        target={store.assignedObjectsArray}
                        onChange={onChange}
                        itemTemplate={itemTemplate}
                        filter
                        showTargetControls={false}
                        showSourceControls={false}
                        filterBy="name"
                        breakpoint="1280px"
                        sourceHeader="Verfügbare Objekte"
                        targetHeader="Zugewiesene Objekte"
                        sourceStyle={{ height: '20rem' }}
                        targetStyle={{ height: '20rem' }}
                        sourceFilterPlaceholder="Search by name"
                        targetFilterPlaceholder="Search by name"
                        data-testid="object-assignment-picklist"
                    />
                ) : null}
            </div>
        </div>
    );
});

const EmployeeOnboarding = observer(({ store }: { store: any }) => {
    return (
        <>
            <div
                className="p-field-checkbox"
                style={{ marginBottom: '1rem' }}
            >
                <Checkbox
                    id={'isOnboardingNow'}
                    inputId="isOnboardingNow"
                    checked={store.isOnboardingNow}
                    onChange={() => store.switchOnboardingNow()}
                    data-testid={'employee-onboarding-checkbox'}
                />
                <label htmlFor="isOnboardingNow">Onboarding sofort aktivieren</label>
            </div>
            {store.isOnboardingNow ? null : (
                <div style={{ marginBottom: '1rem', width: '100%' }}>
                    <label
                        htmlFor="firstname"
                        style={{ display: 'block', marginBottom: '0.5rem' }}
                    >
                        Tag des Onboarding
                    </label>
                    <Calendar
                        id={'onboardingDate'}
                        value={store.onboardingDate}
                        onChange={e => {
                            store.onboardingDate = e.value || null;
                        }}
                        showIcon
                        locale="de"
                        style={{ width: '100%' }}
                        dateFormat={'dd.mm.yy'}
                        data-pr-tooltip="Bitte gebe ein Onboarding-Datum an"
                        data-pr-position="top"
                        data-pr-disabled={store.validOnboarding}
                        invalid={!store.validOnboarding}
                        data-testid={'employee-onboarding-datepicker'}
                    />
                    <Tooltip target="#onboardingDate" />
                </div>
            )}
        </>
    );
});

const EmployeeOffboarding = observer(({ store }: { store: any }) => {
    return (
        <>
            <div
                className="p-field-checkbox"
                style={{ marginBottom: '1rem' }}
            >
                <Checkbox
                    inputId="ifOffboardingNow"
                    checked={store.isOffboardingEnabled}
                    onChange={() => store.switchOffboarding()}
                    data-testid="offboarding-enabled-checkbox"
                />
                <label htmlFor="ifOffboardingNow">Offboarding aktivieren</label>
            </div>
            {store.isOffboardingEnabled ? (
                <div style={{ marginBottom: '1rem', width: '100%' }}>
                    <label
                        htmlFor="firstname"
                        style={{ display: 'block', marginBottom: '0.5rem' }}
                    >
                        Tag des Offboarding
                    </label>
                    <Calendar
                        value={store.offboardingDate}
                        onChange={e => (store.offboardingDate = e.value)}
                        showIcon
                        locale="de"
                        style={{ width: '100%' }}
                        dateFormat={'dd.mm.yy'}
                        data-testid="offboarding-date-picker"
                    />
                </div>
            ) : null}
        </>
    );
});

const EmployeeBasic = observer(({ store }: { store: any }) => {
    const handleInputChangeFirstname = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.value.length <= 40) {
            store.firstname = e.target.value;
        }
    };
    const handleInputChangeLastname = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.value.length <= 40) {
            store.lastname = e.target.value;
        }
    };
    const handleInputChangeReference = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.value.length <= 20) {
            store.referenceId = e.target.value;
        }
    };
    const handleInputChangePhonenumber = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.value.length <= 40) {
            store.phonenumber = e.target.value;
        }
    };

    return (
        <>
            {store.employee_id ? (
                <div style={{ marginBottom: '1rem', width: '100%' }}>
                    <label
                        htmlFor="userns"
                        style={{ display: 'block', marginBottom: '0.5rem' }}
                    >
                        User_Ns
                    </label>
                    <InputText
                        id="userns"
                        placeholder="User_Ns"
                        value={store.employee_userns}
                        disabled={true}
                        style={{ width: '100%' }}
                        data-testid="employee-userns-input"
                    />
                </div>
            ) : null}
            <div style={{ marginBottom: '1rem', width: '100%' }}>
                <label
                    htmlFor="firstname"
                    style={{ display: 'block', marginBottom: '0.5rem' }}
                >
                    Vorname
                </label>
                <InputText
                    id="firstname"
                    placeholder="Vorname"
                    value={store.firstname}
                    onChange={handleInputChangeFirstname}
                    style={{ width: '100%' }}
                    data-pr-tooltip="Der Vorname muss mindestens 3 Zeichen lang sein"
                    data-pr-position="top"
                    data-pr-disabled={store.validFirstname}
                    invalid={!store.validFirstname}
                    data-testid="employee-firstname-input"
                />
                <Tooltip target="#firstname" />
            </div>
            <div style={{ marginBottom: '1rem', width: '100%' }}>
                <label
                    htmlFor="lastname"
                    style={{ display: 'block', marginBottom: '0.5rem' }}
                >
                    Nachname
                </label>
                <InputText
                    id="lastname"
                    placeholder="Nachname"
                    value={store.lastname}
                    onChange={handleInputChangeLastname}
                    style={{ width: '100%' }}
                    data-pr-tooltip="Der Nachname muss mindestens 3 Zeichen lang sein"
                    data-pr-position="top"
                    data-pr-disabled={store.validLastname}
                    invalid={!store.validLastname}
                    data-testid="employee-lastname-input"
                />
                <Tooltip target="#lastname" />
            </div>
            <div style={{ marginBottom: '1rem', width: '100%' }}>
                <label
                    htmlFor="phonenumber"
                    style={{ display: 'block', marginBottom: '0.5rem' }}
                >
                    Rufnummer
                </label>
                <InputText
                    id="phonenumber"
                    placeholder="Rufnummer"
                    value={store.phonenumber}
                    onChange={handleInputChangePhonenumber}
                    style={{ width: '100%' }}
                    data-pr-tooltip="Bitte geben Sie eine gültige Telefonnummer ein: 49(Vorwahl)(Rufnummer"
                    data-pr-position="top"
                    data-pr-disabled={store.validPhonenumber}
                    invalid={!store.validPhonenumber}
                    data-testid="employee-phonenumber-input"
                />
                <Tooltip target="#phonenumber" />
            </div>
            <div style={{ marginBottom: '1rem', width: '100%' }}>
                <label
                    htmlFor="lastname"
                    style={{ display: 'block', marginBottom: '0.5rem' }}
                >
                    Mitarbeiter Nummer
                </label>
                <InputText
                    id="lastname"
                    placeholder="Mitarbeiter Nummer"
                    value={store.referenceId}
                    onChange={handleInputChangeReference}
                    style={{ width: '100%' }}
                    data-pr-tooltip="Die Mitarbeiter Nummer muss zwischen 1 und 20 Zeichen lang sein"
                    data-pr-position="top"
                    data-pr-disabled={store.validReferenceId}
                    invalid={!store.validReferenceId}
                    data-testid="employee-reference-input"
                />
                <Tooltip target="#reference" />
            </div>
        </>
    );
});

const EmployeeTabber = observer(({ store }: { store: any }) => {
    const statusSessionDateTemplate = (rowData: any) => {
        return rowData.training_session_date ? (
            <span>{new Date(rowData.training_session_date).toLocaleDateString('de-DE')}</span>
        ) : (
            '-'
        );
    };

    const imageBodyTemplate = (rowData: any) => {
        return (
            <img
                src={rowData.image ? rowData.image : '/schulung.svg'}
                alt={rowData.name}
                style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '25%',
                }}
            />
        );
    };

    const statusBodyTemplate = (rowData: any) => {
        const getStatusSeverity = (status: any) => {
            if (status === 'completed') {
                return 'success';
            } else if (status === 'failed') {
                return 'danger';
            }
            return 'warning';
        };
        const getStatusText = (status: any) => {
            if (status === 'completed') {
                return 'Bestanden';
            } else if (status === 'failed') {
                return 'Nicht Bestanden';
            } else {
                return 'Offen';
            }
        };
        return (
            <Tag
                value={getStatusText(rowData.training_status)}
                severity={getStatusSeverity(rowData.training_status)}
            />
        );
    };

    return (
        <TabView
            activeIndex={store.activeTab}
            onTabChange={e => {
                store.onChangeTab(e.index);
            }}
        >
            <TabPanel
                header="Objekte"
                data-testid="objects-tab"
            >
                <EmployeeObjectList store={store} />
            </TabPanel>
            <TabPanel
                header="Schulungen"
                data-testid="trainings-tab"
            >
                <DataTable
                    value={store.trainings}
                    emptyMessage="Keine Schulungen gefunden"
                    data-testid="employee-trainings-table"
                >
                    <Column
                        field="image"
                        header=""
                        body={imageBodyTemplate}
                        style={{ maxWidth: '50px' }}
                    />
                    <Column
                        field="name"
                        header="Name"
                        sortable
                    />
                    <Column
                        field="training_status"
                        header="Status"
                        body={statusBodyTemplate}
                        sortable
                    />
                    <Column
                        field="training_session_date"
                        header="Datum"
                        body={statusSessionDateTemplate}
                    />
                </DataTable>
            </TabPanel>
        </TabView>
    );
});

const EmployeeDialog = observer(({ sdk }: { sdk: ApplicationSdk }) => {
    const store = sdk.currentDialog;
    const showSaveButton: boolean = store.isLoading || !store.isValid;

    const handleEmployeeSave = async () => {
        try {
            await store.onSaveEmployee();
            // Erfolgreich => Schließe nun die Ansicht
            sdk.closeDialog({
                event: 'SAVED',
            });
            sdk.showSuccessToast('Reinigungskraft erfolgreich gespeichert');
        } catch (error: any) {
            sdk.showErrorToast(error?.message || 'Unbekannter Fehler');
        }
    };
    return (
        <Dialog
            visible={!!sdk.currentDialog}
            modal
            header={store?.header || ''}
            footer={() => (
                <div>
                    <Button
                        label={store.saveButton}
                        icon="pi pi-check"
                        disabled={showSaveButton}
                        onClick={handleEmployeeSave}
                        data-testid="save-employee-btn"
                    />
                </div>
            )}
            style={{ width: '80rem', height: '50rem' }}
            onHide={() => sdk.closeDialog()}
            data-testid="employee-dialog"
        >
            {store.isLoading ? (
                <div
                    className="flex justify-content-center align-items-center"
                    style={{ height: '100%' }}
                >
                    <ProgressSpinner />
                </div>
            ) : (
                <div className="dialog-content">
                    <div className={'left-column'}>
                        <div className={'card'}>
                            <EmployeeBasic store={store} />
                            <EmployeeOnboarding store={store} />
                            <EmployeeOffboarding store={store} />
                            <EmployeeRights store={store} />
                        </div>
                    </div>
                    <div className={'right-column'}>
                        <div className={'card'}>
                            <EmployeeTabber store={store} />
                        </div>
                    </div>
                </div>
            )}
        </Dialog>
    );
});

export default EmployeeDialog;
